import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, XCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface CheckResult {
  category: string;
  test: string;
  status: 'pass' | 'fail' | 'warning';
  message: string;
  critical: boolean;
}

const ProductionReadinessChecker: React.FC = () => {
  const [results, setResults] = useState<CheckResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  const runCheck = async (category: string, test: string, checkFn: () => Promise<CheckResult>) => {
    try {
      const result = await checkFn();
      setResults(prev => [...prev, result]);
      return result;
    } catch (error: any) {
      const failResult: CheckResult = {
        category,
        test,
        status: 'fail',
        message: error.message || 'Test failed',
        critical: true
      };
      setResults(prev => [...prev, failResult]);
      return failResult;
    }
  };

  const runAllChecks = async () => {
    setIsRunning(true);
    setResults([]);
    setProgress(0);

    const totalChecks = 12;
    let currentCheck = 0;

    const updateProgress = () => {
      currentCheck++;
      setProgress((currentCheck / totalChecks) * 100);
    };

    try {
      // 1. Database Security Check
      await runCheck('Security', 'RLS Enabled', async () => {
        try {
          // Check specific tables directly without dynamic queries
          const { data: adminTable, error: adminError } = await supabase.from('admin_users').select('id').limit(1);
          const { data: contactTable, error: contactError } = await supabase.from('contact_submissions').select('id').limit(1);
          const { data: careTable, error: careError } = await supabase.from('care_profiles').select('id').limit(1);
          
          const errors = [adminError, contactError, careError].filter(Boolean);
          
          return {
            category: 'Security',
            test: 'RLS Enabled',
            status: errors.length === 0 ? 'pass' : 'warning',
            message: errors.length === 0 
              ? 'Core tables accessible with RLS' 
              : `${errors.length} table access issues`,
            critical: false
          };
        } catch (error: any) {
          return {
            category: 'Security',
            test: 'RLS Enabled',
            status: 'fail',
            message: error.message,
            critical: true
          };
        }
      });
      updateProgress();

      // 2. Edge Functions Health
      await runCheck('Edge Functions', 'SEO Fetch', async () => {
        const { data, error } = await supabase.functions.invoke('seo-fetch', {
          body: { action: 'sitemap' }
        });
        return {
          category: 'Edge Functions',
          test: 'SEO Fetch',
          status: !error ? 'pass' : 'fail',
          message: !error ? 'SEO fetch function responsive' : `Error: ${error.message}`,
          critical: false
        };
      });
      updateProgress();

      // 3. Form Submission Test
      await runCheck('Forms', 'Contact Form', async () => {
        const { data, error } = await supabase.functions.invoke('send-contact-email', {
          body: {
            firstName: 'Test',
            lastName: 'User',
            email: 'test@example.com',
            subject: 'Production Test',
            message: 'Automated production readiness test',
            phone: '555-0123'
          }
        });
        return {
          category: 'Forms',
          test: 'Contact Form',
          status: !error ? 'pass' : 'fail',
          message: !error ? 'Contact form processing works' : `Error: ${error.message}`,
          critical: true
        };
      });
      updateProgress();

      // 4. Newsletter Integration
      await runCheck('Integrations', 'Newsletter Service', async () => {
        const { data, error } = await supabase.functions.invoke('newsletter-subscribe', {
          body: {
            email: 'test@example.com',
            firstName: 'Test',
            lastName: 'User',
            newsletterType: 'family'
          }
        });
        return {
          category: 'Integrations',
          test: 'Newsletter Service',
          status: !error ? 'pass' : 'fail',
          message: !error ? 'Newsletter subscription works' : `Error: ${error.message}`,
          critical: false
        };
      });
      updateProgress();

      // 5. Professional Applications
      await runCheck('Forms', 'Professional Applications', async () => {
        const { data, error } = await supabase.functions.invoke('submit-professional-application', {
          body: {
            profession: 'OT',
            first_name: 'Test',
            last_name: 'Professional',
            email: 'test-pro@example.com',
            phone: '555-0123',
            license_state: 'CA',
            terms_agreed: true
          }
        });
        return {
          category: 'Forms',
          test: 'Professional Applications',
          status: !error ? 'pass' : 'fail',
          message: !error ? 'Professional application works' : `Error: ${error.message}`,
          critical: true
        };
      });
      updateProgress();

      // 6. Database Tables Structure
      await runCheck('Database', 'Core Tables', async () => {
        const tables = ['contact_submissions', 'admin_users', 'seo_metrics', 'care_profiles'] as const;
        let allExist = true;
        let missing = [];

        for (const table of tables) {
          try {
            const { error } = await (supabase as any).from(table).select('id').limit(1);
            if (error && error.message.includes('does not exist')) {
              allExist = false;
              missing.push(table);
            }
          } catch (e) {
            // Table access issues aren't necessarily missing tables
            // Could be RLS blocking access, which is expected
          }
        }

        return {
          category: 'Database',
          test: 'Core Tables',
          status: missing.length === 0 ? 'pass' : 'fail',
          message: missing.length === 0 ? 'All core tables accessible' : `Issues with tables: ${missing.join(', ')}`,
          critical: missing.length > 2 // Only critical if many tables are problematic
        };
      });
      updateProgress();

      // 7. Admin Authentication
      await runCheck('Security', 'Admin System', async () => {
        const { data, error } = await supabase.from('admin_users').select('id').limit(1);
        return {
          category: 'Security',
          test: 'Admin System',
          status: !error ? 'pass' : 'fail',
          message: !error ? 'Admin system accessible' : `Error: ${error.message}`,
          critical: false
        };
      });
      updateProgress();

      // 8. SEO Tools
      await runCheck('SEO', 'Monitoring Tools', async () => {
        const { data, error } = await supabase.from('seo_metrics').select('*').limit(1);
        const { data: issues } = await supabase.from('seo_issues').select('*').limit(1);
        return {
          category: 'SEO',
          test: 'Monitoring Tools',
          status: !error ? 'pass' : 'fail',
          message: !error ? 'SEO monitoring system ready' : `Error: ${error.message}`,
          critical: false
        };
      });
      updateProgress();

      // 9. Storage Buckets
      await runCheck('Storage', 'File Upload System', async () => {
        const { data, error } = await supabase.storage.listBuckets();
        const requiredBuckets = ['assets', 'community-feed-images', 'profile-pictures'];
        const existingBuckets = data?.map(b => b.name) || [];
        const missingBuckets = requiredBuckets.filter(b => !existingBuckets.includes(b));
        
        return {
          category: 'Storage',
          test: 'File Upload System',
          status: missingBuckets.length === 0 ? 'pass' : 'warning',
          message: missingBuckets.length === 0 
            ? 'All storage buckets available' 
            : `Missing buckets: ${missingBuckets.join(', ')}`,
          critical: false
        };
      });
      updateProgress();

      // 10. Performance Check
      await runCheck('Performance', 'Response Times', async () => {
        const start = Date.now();
        const { error } = await supabase.from('contact_submissions').select('id').limit(5);
        const duration = Date.now() - start;
        
        return {
          category: 'Performance',
          test: 'Response Times',
          status: duration < 2000 ? 'pass' : 'warning',
          message: `Database query: ${duration}ms`,
          critical: false
        };
      });
      updateProgress();

      // 11. Error Handling
      await runCheck('Error Handling', 'Graceful Failures', async () => {
        try {
          // Test error handling by accessing admin table without auth
          const { error } = await supabase.from('admin_users').select('*');
          return {
            category: 'Error Handling',
            test: 'Graceful Failures',
            status: error ? 'pass' : 'warning',
            message: error ? 'RLS properly blocks unauthorized access' : 'Security check unclear',
            critical: false
          };
        } catch (e: any) {
          return {
            category: 'Error Handling',
            test: 'Graceful Failures',
            status: 'pass',
            message: 'Exceptions caught properly',
            critical: false
          };
        }
      });
      updateProgress();

      // 12. Final Security Scan
      await runCheck('Security', 'Final Security Check', async () => {
        try {
          // Simple security check - attempt to access protected data
          const { error } = await supabase.from('admin_users').select('id').limit(1);
          
          return {
            category: 'Security',
            test: 'Final Security Check',
            status: error ? 'pass' : 'warning',
            message: error ? 'Security policies active' : 'Security validation needs review',
            critical: false
          };
        } catch (e: any) {
          return {
            category: 'Security',
            test: 'Final Security Check',
            status: 'warning',
            message: 'Security check completed with exceptions',
            critical: false
          };
        }
      });
      updateProgress();

      const criticalFails = results.filter(r => r.status === 'fail' && r.critical).length;
      const totalFails = results.filter(r => r.status === 'fail').length;
      
      if (criticalFails > 0) {
        toast.error(`❌ ${criticalFails} critical issues found - NOT ready for production`);
      } else if (totalFails > 0) {
        toast.warning(`⚠️ ${totalFails} non-critical issues found - Review before production`);
      } else {
        toast.success('✅ All checks passed - Ready for production!');
      }

    } catch (error: any) {
      toast.error('Production readiness check failed', { description: error.message });
    } finally {
      setIsRunning(false);
      setProgress(100);
    }
  };

  const getStatusIcon = (status: CheckResult['status']) => {
    switch (status) {
      case 'pass': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'fail': return <XCircle className="h-4 w-4 text-red-600" />;
    }
  };

  const getStatusBadge = (status: CheckResult['status']) => {
    switch (status) {
      case 'pass': return <Badge className="bg-green-100 text-green-800">Pass</Badge>;
      case 'warning': return <Badge variant="outline" className="border-yellow-500 text-yellow-700">Warning</Badge>;
      case 'fail': return <Badge variant="destructive">Fail</Badge>;
    }
  };

  const criticalFails = results.filter(r => r.status === 'fail' && r.critical).length;
  const totalTests = results.length;
  const passedTests = results.filter(r => r.status === 'pass').length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Production Readiness Check
          <Button 
            onClick={runAllChecks} 
            disabled={isRunning}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isRunning ? 'animate-spin' : ''}`} />
            {isRunning ? 'Running...' : 'Run Full Check'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isRunning && (
          <div>
            <Progress value={progress} className="mb-2" />
            <p className="text-sm text-muted-foreground">
              Running production readiness checks...
            </p>
          </div>
        )}

        {results.length > 0 && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{passedTests}</div>
                <div className="text-sm text-muted-foreground">Passed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {results.filter(r => r.status === 'warning').length}
                </div>
                <div className="text-sm text-muted-foreground">Warnings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {results.filter(r => r.status === 'fail').length}
                </div>
                <div className="text-sm text-muted-foreground">Failed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{criticalFails}</div>
                <div className="text-sm text-muted-foreground">Critical</div>
              </div>
            </div>

            <div className="space-y-2">
              {Object.entries(
                results.reduce((acc, result) => {
                  if (!acc[result.category]) acc[result.category] = [];
                  acc[result.category].push(result);
                  return acc;
                }, {} as Record<string, CheckResult[]>)
              ).map(([category, categoryResults]) => (
                <div key={category} className="border rounded-lg p-3">
                  <h4 className="font-bold mb-2">{category}</h4>
                  <div className="space-y-1">
                    {categoryResults.map((result, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(result.status)}
                          <span className="text-sm">{result.test}</span>
                          {result.critical && <Badge variant="outline" className="text-xs">Critical</Badge>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{result.message}</span>
                          {getStatusBadge(result.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 p-3 rounded-lg bg-muted">
              <h4 className="font-bold mb-2">Production Readiness Status</h4>
              {criticalFails > 0 ? (
                <div className="text-red-600 font-medium">
                  ❌ NOT READY - {criticalFails} critical issue(s) must be resolved
                </div>
              ) : totalTests - passedTests > 0 ? (
                <div className="text-yellow-600 font-medium">
                  ⚠️ REVIEW NEEDED - {totalTests - passedTests} issue(s) should be addressed
                </div>
              ) : (
                <div className="text-green-600 font-medium">
                  ✅ READY FOR PRODUCTION - All critical checks passed!
                </div>
              )}
            </div>
          </>
        )}

        {results.length === 0 && !isRunning && (
          <div className="text-center py-8 text-muted-foreground">
            Click "Run Full Check" to verify production readiness
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductionReadinessChecker;